import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Plus, Pencil, Trash2, Package } from 'lucide-react'
import { Modal } from '../common/Modal'
import { PainelMovel } from './PainelMovel'

import { formatCurrency } from '../../utils/fmt'

function adicionaisBadges(adicionais) {
  if (!adicionais) return []
  const badges = []
  const bools = { rodinhas:'Rodinhas', pePalito:'Pé palito', baseMadeira:'Base madeira',
                  baseMetalon:'Base metalon', moldura:'Moldura', espessura25mm:'Esp. 25mm' }
  const qtds  = { puxadorPonto:'P. ponto', puxadorAlca:'P. alça', puxadorConcha:'P. concha',
                  gavetaBranco:'Gav. branco', gavetaCor:'Gav. cor', chaves:'Chaves',
                  prateleiraBranco:'Prat. branco', prateleiraCor:'Prat. cor' }
  Object.entries(bools).forEach(([k, l]) => { if (adicionais[k]) badges.push(l) })
  Object.entries(qtds).forEach(([k, l])  => { if (adicionais[k] > 0) badges.push(`${l}: ${adicionais[k]}`) })
  return badges
}

export function StepMoveis() {
  const { watch, setValue, formState: { errors } } = useFormContext()
  const moveis  = watch('moveis') || []
  const prazo   = watch('prazo')

  const [modalAberto, setModalAberto]   = useState(false)
  const [editando,    setEditando]      = useState(null)

  const total = moveis.reduce((acc, m) => acc + Number(m.preco || 0), 0)

  function abrirNovo() {
    setEditando(null)
    setModalAberto(true)
  }

  function abrirEditar(movel) {
    setEditando(movel)
    setModalAberto(true)
  }

  function salvarMovel(movel) {
    const lista = editando
      ? moveis.map(m => m.id === movel.id ? movel : m)
      : [...moveis, movel]
    setValue('moveis', lista, { shouldValidate: true })
    setModalAberto(false)
    setEditando(null)
  }

  function removerMovel(id) {
    setValue('moveis', moveis.filter(m => m.id !== id), { shouldValidate: true })
  }

  return (
    <div className="space-y-5">
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Pedido</h3>

      {/* Prazo */}
      <div className="max-w-xs">
        <label className="label">Prazo de entrega *</label>
        <input
          type="date"
          value={prazo || ''}
          onChange={e => setValue('prazo', e.target.value)}
          className={`input ${errors.prazo ? 'input-error' : ''}`}
        />
        {errors.prazo && <p className="error-msg">{errors.prazo.message}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">
              Móveis do pedido
              {moveis.length > 0 && (
                <span className="ml-2 bg-[#FFF9C2] text-[#CC8800] text-xs font-semibold px-2 py-0.5 rounded-full">
                  {moveis.length}
                </span>
              )}
            </p>
            {moveis.length > 0 && (
              <p className="text-xs text-slate-400 mt-0.5">
                Total: <span className="font-semibold text-slate-700">{formatCurrency(total)}</span>
              </p>
            )}
          </div>
          <button type="button" onClick={abrirNovo} className="btn-primary text-sm">
            <Plus size={15} />
            Adicionar móvel
          </button>
        </div>

        {errors.moveis && (
          <p className="error-msg mb-2">{errors.moveis.message}</p>
        )}

        {moveis.length === 0 ? (
          <div
            onClick={abrirNovo}
            className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center cursor-pointer hover:border-[#FFD900] hover:bg-[#FFFDE7]/40 transition-all group"
          >
            <Package className="mx-auto text-slate-300 group-hover:text-[#F5C200] transition-colors mb-3" size={36} />
            <p className="text-sm text-slate-400 group-hover:text-[#FFFDE7]0 font-medium">
              Clique para adicionar o primeiro móvel
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {moveis.map((m, i) => {
              const badges = adicionaisBadges(m.adicionais)
              return (
                <div key={m.id} className="card p-4 flex items-start gap-4 group">
                  <div className="w-8 h-8 rounded-lg bg-[#FFF9C2] text-[#CC8800] flex items-center justify-center text-sm font-bold shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{m.modelo}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Cor: <span className="font-medium">{m.cor}</span>
                          {(m.comprimento || m.altura || m.profundidade) && (
                            <span className="ml-2">
                              {[m.comprimento, m.profundidade, m.altura].filter(Boolean).join(' × ')} cm
                            </span>
                          )}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-[#CC8800] shrink-0">{formatCurrency(m.preco)}</p>
                    </div>
                    {badges.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {badges.map(b => (
                          <span key={b} className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
                            {b}
                          </span>
                        ))}
                      </div>
                    )}
                    {m.observacoes && (
                      <p className="text-xs text-slate-400 mt-1 italic">"{m.observacoes}"</p>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      type="button"
                      onClick={() => abrirEditar(m)}
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removerMovel(m.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <Modal
        open={modalAberto}
        onClose={() => { setModalAberto(false); setEditando(null) }}
        title={editando ? 'Editar móvel' : 'Adicionar móvel'}
        size="lg"
      >
        <PainelMovel
          inicial={editando}
          onSalvar={salvarMovel}
          onCancelar={() => { setModalAberto(false); setEditando(null) }}
        />
      </Modal>
    </div>
  )
}
